export interface SalaryConfig {
  allowRemove: boolean;
  showHistory: boolean;
  createdAt?: Date;
  createdBy?: string; // User ID
  updatedAt?: Date;
  updatedBy?: string; // User ID
}

export interface LegacyCurrentSalarySnapshot {
  amount: number;
  extras: number;
  salaryType: 'CLT' | 'PJ' | 'Estagiário' | 'Sócio';
  effectiveDate: string; // ISO date string
}

// Salary data returned from Firestore (config + optional legacy snapshot)
export interface CollaboratorSalary {
  config: SalaryConfig | null;
  legacyCurrent?: LegacyCurrentSalarySnapshot | null;
}

// Salary history entry stored in subcollection
export interface SalaryHistoryEntry {
  // Salary Data at Time of Change
  amount: number;
  extras: number;
  salaryType: 'CLT' | 'PJ' | 'Estagiário' | 'Sócio';

  // Change Information
  changeDate: string; // ISO date string
  changeReason: string;
  changeObservations?: string;

  // Audit Fields (Auto-populated)
  recordedAt: Date; // Server timestamp when entry was created
  recordedBy: string; // User ID who made the change
}

// Form data structure for UI
export interface SalaryFormData {
  // New Entry Fields
  newSalaryEntryAmount: number;
  newSalaryEntryDate: string;
  newSalaryEntryExtras: number;
  newSalaryEntryObservations: string;
  newSalaryEntryReason: string;
  newSalaryEntrySalaryType: 'CLT' | 'PJ' | 'Estagiário' | 'Sócio';

  // History Configuration
  salaryHistoryAllowRemove: boolean;
  salaryHistoryShowHistory: boolean;
}

// Permission types
export type SalaryPermissionRole = 'admin' | 'salary_admin' | 'salary_view';

export interface SalaryPermissions {
  canViewSalary: boolean;
  canEditSalary: boolean;
  canDeleteSalary: boolean;
}

// Permission matrix constant
export const PERMISSION_MATRIX: Record<SalaryPermissionRole, SalaryPermissions> = {
  admin: {
    canViewSalary: true,
    canEditSalary: true,
    canDeleteSalary: true,
  },
  salary_admin: {
    canViewSalary: true,
    canEditSalary: true,
    canDeleteSalary: true,
  },
  salary_view: {
    canViewSalary: true,
    canEditSalary: false,
    canDeleteSalary: false,
  },
};

// Migration tracking
export interface SalaryMigrationLog {
  collaboratorId: string;
  collaboratorName: string;

  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  fieldsExtracted: string[];

  startedAt?: Date;
  completedAt?: Date;
  errorMessage?: string;

  // Audit
  migratedBy: string; // Admin user ID who triggered migration
}

// Type guards
export const isSalaryPermissionRole = (role: string): role is SalaryPermissionRole => {
  return ['admin', 'salary_admin', 'salary_view'].includes(role);
};

// Helper to check if user has salary permission
export const hasSalaryPermission = (
  roles: string[],
  permission: keyof SalaryPermissions
): boolean => {
  if (!roles || roles.length === 0) return false;

  for (const role of roles) {
    if (isSalaryPermissionRole(role)) {
      if (PERMISSION_MATRIX[role][permission]) {
        return true;
      }
    }
  }

  // Admin role always has full permissions
  if (roles.includes('admin')) {
    return true;
  }

  return false;
};
